import React, { useState, useEffect } from "react";
import {
    Card,
    Typography,
    Spin,
    message,
} from "antd";
import {
    SettingOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "../../../services/api";
import QuestionsList from "./QuestionsList";
import CommonSelect from "../../../core/common/commonSelect";

const { Title, Text } = Typography;

interface CompanyType {
    id: string | null;
    type: string | null;
    name: string;
}

const MoodQuestionsPage: React.FC = () => {
    const signature = localStorage.getItem("signature");
    const user = useSelector((state: any) => state.user.user);
    const userRole = user?.userDetails?.role_char;

    // State
    const [selectedCompany, setSelectedCompany] = useState<CompanyType>({
        id: user.company_id,
        type: localStorage.account_type,
        name: "",
    });
    const [companies, setCompanies] = useState<any[]>([]);
    const [companyOptions, setCompanyOptions] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // Fetch companies
    const fetchCompanies = async () => {
        try {
            const response = await api.post("userService/getAllCompanyList", {
                signature,
            });

            if (response.data?.msg === "Data Found") {
                const companiesData = response.data.data?.data || [];
                setCompanies(companiesData);

                const formattedOptions = companiesData.map((company: any) => ({
                    value: `${company.id}-${company.type}`,
                    label: `${company.name} - ${company.type}`,
                    companyName: company.name,
                    companyId: company.id,
                    companyType: company.type,
                }));
                setCompanyOptions(formattedOptions);

                // Auto-select if not already selected
                if (!selectedCompany.id && companiesData.length > 0) {
                    const firstCompany = companiesData[0];
                    setSelectedCompany({
                        id: firstCompany.id,
                        type: firstCompany.type,
                        name: firstCompany.name,
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
            message.error("Failed to load companies");
        } finally {
            setLoading(false);
        }
    };

    // Handle company change
    const handleCompanyChange = (selectedOption: any) => {
        if (!selectedOption) return;

        const [companyId, companyType] = selectedOption.value.split('-');
        const companyName = selectedOption.companyName;

        setSelectedCompany({
            id: companyId,
            type: companyType,
            name: companyName,
        });

        // Save to localStorage for persistence
        localStorage.setItem("selectedCompanyId", companyId);
        localStorage.setItem("selectedCompanyType", companyType);
        localStorage.setItem("selectedCompanyName", companyName);

        message.success(`Switched to ${companyName}`);
    };

    const getCurrentCompanyValue = () => {
        if (!selectedCompany.id || !selectedCompany.type) {
            return companyOptions[0] || null;
        }

        const currentValue = `${selectedCompany.id}-${selectedCompany.type}`;
        return companyOptions.find(option => option.value === currentValue) || companyOptions[0];
    };

    useEffect(() => {
        if (signature) {
            fetchCompanies();
        }
    }, [signature]);

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1" style={{ fontWeight: 600 }}>
                            <SettingOutlined style={{ marginRight: 10 }} />
                            Manage Mood Questions
                        </h2>
                    </div>
                    <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                        <div
                            className="dropdown me-2 mb-2"
                            title="Choose Company"
                            style={{ minWidth: "200px" }}
                        >
                            <CommonSelect
                                className="select"
                                options={companyOptions}
                                defaultValue={getCurrentCompanyValue()}
                                onChange={handleCompanyChange}
                                placeHolder="Select Company"
                                isDisabled={loading}
                            />
                        </div>
                    </div>
                </div>

                {/* Company Selection Info */}
                {!selectedCompany.id && (
                    <Card className="mb-3" size="small">
                        <div className="text-center py-3">
                            <SettingOutlined style={{ fontSize: 24, marginBottom: 10 }} />
                            <p className="mb-0">Please select a company to manage questions</p>
                        </div>
                    </Card>
                )}

                {/* Main Content */}
                {loading ? (
                    <div className="text-center py-5">
                        <Spin size="large" />
                        <div className="mt-3">
                            <Text type="secondary">Loading...</Text>
                        </div>
                    </div>
                ) : (
                    selectedCompany.id && (
                        <Card className="shadow-sm">
                            <QuestionsList
                                selectedCompany={selectedCompany}
                                onQuestionsChange={() => {
                                    message.info("Questions updated");
                                }}
                            />
                        </Card>
                    )
                )}

                {/* Footer */}
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2025 © CMS.</p>
                    <p>
                        Designed & Developed By{" "}
                        <Link to="#" className="text-primary">
                            ZYVO
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MoodQuestionsPage;